import { useSyncExternalStore } from 'react'
import { registerSW } from 'virtual:pwa-register'

/**
 * The app's single service-worker registration, imported for its side effect
 * from `main.tsx` so registration happens on every load — including loads
 * where the shell never renders (a stale bundle stuck on an error screen is
 * exactly the client that most needs to see the new build).
 *
 * A module singleton rather than `useRegisterSW`: that hook registers per
 * mounted component, and both the update banner (UpdatePrompt) and the
 * broken-shell recovery path (useStaleBundleRecovery) need the same
 * registration + `needRefresh` signal.
 */

/** Set once a SW has registered; UpdatePrompt reads it to tell a genuine
 *  update apart from the very first install. */
export const INSTALLED_KEY = 'polf-pwa-installed'

let needRefresh = false
const listeners = new Set<() => void>()

export const updateServiceWorker = registerSW({
  immediate: true,
  onNeedRefresh() {
    needRefresh = true
    listeners.forEach((notify) => notify())
  },
  onRegisteredSW() {
    try {
      localStorage.setItem(INSTALLED_KEY, '1')
    } catch {
      // Storage blocked — the update banner stays suppressed on this device.
    }
  },
})

const getNeedRefresh = () => needRefresh

const subscribeNeedRefresh = (notify: () => void) => {
  listeners.add(notify)
  return () => {
    listeners.delete(notify)
  }
}

/** True once a new SW is installed and waiting (prompt-mode `needRefresh`). */
export const useNeedRefresh = (): boolean =>
  useSyncExternalStore(subscribeNeedRefresh, getNeedRefresh)

/** Set once a new SW has taken over this page (`clientsClaim`). From then
 *  on a reload is served by the new build. */
let controllerChanged = false
navigator.serviceWorker?.addEventListener('controllerchange', () => {
  controllerChanged = true
})

/**
 * Resolves once a reload will land on the newest build. A plain reload is not
 * enough while a SW controls the page: it would be answered from the old
 * precache. So this kicks an update check, skip-waits a waiting SW if there
 * is one, and resolves when the new SW has claimed the page. Rejects after
 * `timeoutMs` without one. Nothing here disturbs the page, so the version
 * watch (main.tsx) runs it before judging whether a reload is safe.
 */
export const prepareAppUpdate = (timeoutMs = 60_000): Promise<void> =>
  new Promise((resolve, reject) => {
    const sw = navigator.serviceWorker
    if (!sw?.controller || controllerChanged) {
      resolve()
      return
    }
    sw.addEventListener('controllerchange', () => resolve(), { once: true })
    setTimeout(() => reject(new Error('no new service worker')), timeoutMs)
    sw.getRegistration()
      .then((reg) => reg?.update())
      .catch(() => {})
    const skipWaiting = () => {
      updateServiceWorker(true).catch(() => {})
    }
    if (needRefresh) skipWaiting()
    else subscribeNeedRefresh(skipWaiting)
  })

/**
 * Escape hatch for a shell that can't render: get onto the newest build now,
 * falling back to a plain reload if no new SW takes over within `fallbackMs`.
 */
export const forceAppUpdate = (fallbackMs = 8000): void => {
  prepareAppUpdate(fallbackMs).finally(() => window.location.reload())
}
